import moment from "moment-timezone";
import i18n from "@/i18n";

export default class TimeService {
  static timezoneList = moment.tz.names();
  static getTimeZoneList(): String[] {
    return this.timezoneList;
  }

  static getDateDisplayStringWithWeekDay(date: Date): string {
    return date.toDateString();
  }

  static addDaysToDate(date: Date, days: number): Date {
    const result = new Date(date);
    result.setDate(date.getDate() + days);
    return result;
  }

  static addMinutesToDate(date: Date, minutes: number): Date {
    const result = new Date(date);
    result.setTime(date.getTime() + minutes * 60 * 1000);
    return result;
  }

  static dateTimesEqual(date1: Date, date2: Date): boolean {
    return date1.getTime() === date2.getTime();
  }

  static getDateAtStartOfDay() {
    const date = new Date();
    date.setHours(0, 0, 0, 0);
    return date;
  }

  static setDateToStartOfDay(date: Date) {
    date.setHours(0, 0, 0, 0);
    return date;
  }

  static setDateHours(date: Date, time: string) {
    if (!time.match("^[0-9]{2}:[0-9]{2}$")) {
      console.log("Invalid Time used in SetDateHours: " + time);
    }
    const split = time.split(":");
    date.setHours(parseInt(split[0], 10), parseInt(split[1], 10), 0, 0);
  }

  static weekDays = [
    { text: i18n.t("date.sunday"), value: "SUNDAY" },
    { text: i18n.t("date.monday"), value: "MONDAY" },
    { text: i18n.t("date.tuesday"), value: "TUESDAY" },
    { text: i18n.t("date.wednesday"), value: "WEDNESDAY" },
    { text: i18n.t("date.thursday"), value: "THURSDAY" },
    { text: i18n.t("date.friday"), value: "FRIDAY" },
    { text: i18n.t("date.saturday"), value: "SATURDAY" }
  ];

  static getISODateString(date: Date): string {
    return this.buildISODateString(
      date.getFullYear(),
      date.getMonth(),
      date.getDate()
    );
  }

  static getISO8601DateTimeString(date: Date): string {
    return this.getISOString(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
      date.getHours(),
      date.getMinutes()
    );
    // return date.toISOString().slice(0, -8); /* Removes seconds, nano seconds and any trailing info  eg: 2021-03-30T19:45 */
  }

  static getNonISODisplayDateString(date: Date): string {
    return (
      this.padWith0(date.getDate()) +
      "-" +
      this.formatMonth(date.getMonth()) +
      "-" +
      date.getFullYear().toString()
    );
  }

  static dateIsBeforeOrEqual(date: Date, endDate: Date) {
    if (date.getFullYear() > endDate.getFullYear()) return false;
    if (date.getFullYear() < endDate.getFullYear()) return true;
    if (date.getMonth() > endDate.getMonth()) return false;
    if (date.getMonth() < endDate.getMonth()) return true;
    return date.getDate() <= endDate.getDate();
  }

  static dateIsAfterOrEqual(date: Date, startDate: Date) {
    if (date.getFullYear() < startDate.getFullYear()) return false;
    if (date.getFullYear() > startDate.getFullYear()) return true;
    if (date.getMonth() < startDate.getMonth()) return false;
    if (date.getMonth() > startDate.getMonth()) return true;
    return date.getDate() >= startDate.getDate();
  }

  static dateTimeIsAfterOrEqual(date: Date, startDate: Date) {
    if (date.getFullYear() < startDate.getFullYear()) return false;
    if (date.getFullYear() > startDate.getFullYear()) return true;
    if (date.getMonth() < startDate.getMonth()) return false;
    if (date.getMonth() > startDate.getMonth()) return true;
    if (date.getDate() < startDate.getDate()) return false;
    if (date.getDate() > startDate.getDate()) return true;
    if (date.getHours() < startDate.getHours()) return false;
    if (date.getHours() > startDate.getHours()) return true;
    return date.getMinutes() >= startDate.getMinutes();
  }

  private static getISOString(
    year: number,
    month: number,
    day: number,
    hour: number,
    minute: number
  ): string {
    return (
      this.buildISODateString(year, month, day) +
      "T" +
      this.getTimeISOString(hour, minute)
    );
  }

  private static getTimeISOString(hour: number, minute: number): string {
    return this.padWith0(hour) + ":" + this.padWith0(minute);
  }

  private static buildISODateString(
    year: number,
    month: number,
    day: number
  ): string {
    return year + "-" + this.formatMonth(month) + "-" + this.padWith0(day);
  }

  private static formatMonth(month: number): string {
    return this.padWith0(month + 1);
  }

  static getMonth(date: Date) {
    return TimeService.formatMonth(date.getMonth());
  }

  static getYear(date: Date) {
    return date.getFullYear();
  }

  private static padWith0(num: number): string {
    if (num < 10) return "0" + num;
    return num.toString();
  }

  static getTimeFromISOString(isoString: string): string {
    const date = new Date(isoString);
    return this.getTimeISOString(date.getHours(), date.getMinutes());
  }

  static getDateTimeDisplayStringFromISO(isoString: string): string {
    return (
      TimeService.getDateDisplayStringWithWeekDay(new Date(isoString)) +
      " " +
      TimeService.getTimeFromISOString(isoString)
    );
  }

  static getTimesBetweenWithInterval(
    startTime: string,
    endTime: String,
    interval: number
  ): string[] {
    if (
      startTime.match("^(?:\\d|[01]\\d|2[0-3]):[0-5]\\d$") &&
      endTime.match("^(?:\\d|[01]\\d|2[0-3]):[0-5]\\d$") &&
      interval <= 60
    ) {
      const times: string[] = [];
      let startDT = new Date();
      const endDT = new Date();
      let timeParts = startTime.split(":");

      startDT.setUTCHours(parseInt(timeParts[0]), parseInt(timeParts[1]));

      timeParts = endTime.split(":");
      endDT.setUTCHours(parseInt(timeParts[0]), parseInt(timeParts[1]));

      while (this.dateTimeIsAfterOrEqual(endDT, startDT)) {
        times.push(
          this.getTimeISOString(startDT.getUTCHours(), startDT.getUTCMinutes())
        );
        startDT = this.addMinutesToDate(startDT, interval);
      }
      return times;
    } else {
      console.error(
        "TimeService:getTimesBetweenWithInterval: invalid times: " +
          startTime +
          " and " +
          endTime +
          " with interval: " +
          interval
      );
      return ["Error"];
    }
  }
}
